import { Component } from "@angular/core";

import { App } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';
import { Network } from '@capacitor/network';
import { Geolocation } from '@capacitor/geolocation';

import { ModalController, NavController, Platform, ToastController } from "@ionic/angular";
import { StatusBar } from "@ionic-native/status-bar/ngx";
import { UniqueDeviceID } from "@ionic-native/unique-device-id/ngx";
import { ObservablesService } from "./services/observables.service";
import { EnumService } from "./services/enum.service";
import { SharedDataService } from "./services/shared-data.service";
import { UtilService } from "./services/util.service";
import { ScreenOrientation } from "@ionic-native/screen-orientation/ngx";
import { AccountService } from "./services/account.service";
import { ApiService } from "./services/api.service";
import { NgZone } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { filter } from "rxjs/operators";
import { Response, User } from "./_models";
import { Badge } from "@ionic-native/badge/ngx";
import { HttpClient } from "@angular/common/http";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { TranslateService } from "@ngx-translate/core";
import { environment } from "src/environments/environment";
import { OfflineManagerService } from "./services/offline-manager.service";
import { DeviceOfflinePage } from "./modals/device-offline/device-offline.page";
import { DeviceDetailData } from "./_models/offline/DeviceDetailData";
import { Storage } from "@ionic/storage-angular";
import { Device as deviceID } from '@ionic-native/device/ngx';
import { BackgroundSyncService } from "./services/background-sync.service";
import { AppUpdateService } from "./services/app-update.service";

//const { Geolocation, Permissions, App, SplashScreen, Network } = Plugins;

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, "./assets/i18n/", ".json");
}

@Component({
  selector: "app-root",
  templateUrl: "app.component.html",
  styleUrls: ["app.component.scss"],
})
export class AppComponent {
  private readonly OFFLINE_DATASET_POPUP_ROUTES = [
    "dashboard-dm",
    "documents-dm",
    "forms-dm",
  ];

  deviceOfflineModal: HTMLIonModalElement = null;
  offlineNotEnabledModal: HTMLIonModalElement = null;
  private offlineNotEnabledModalCreating = false;
  private previousNetworkStatus: boolean = true;
  private incompleteSyncCheckGeneration = 0;

  constructor(
    private platform: Platform,
    private storage: Storage,
    private ngZone: NgZone,
    private statusBar: StatusBar,
    private uniqueDeviceID: UniqueDeviceID,
    public sharedDataService: SharedDataService,
    public utilService: UtilService,
    private observablesService: ObservablesService,
    private offlineManagerService: OfflineManagerService,
    private navController: NavController,
    private screenOrientation: ScreenOrientation,
    private accountService: AccountService,
    private apiService: ApiService,
    private router: Router,
    private badge: Badge,
    private modalController: ModalController,
    private translateService: TranslateService,
    private device: deviceID,
    private backgroundSyncService: BackgroundSyncService,
    private toastController: ToastController,
    private appUpdateService: AppUpdateService
  ) {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        if (
          this.deviceOfflineModal &&
          !this.isOfflineDatasetPopupAllowedRoute()
        ) {
          this.dismissDeviceOfflineModalSafely();
        }
      });

    this.initializeApp();

    if (environment.isFormPreview || UtilService.isWebApp()) {
      localStorage.setItem(
        EnumService.LocalStorageKeys.WINDOW_LOCATION,
        window.location.href
      );
    }

    // Expose BackgroundSyncService to window for debugging SignalR
    if (typeof window !== 'undefined') {
      (window as any).backgroundSyncService = this.backgroundSyncService;
      console.log('💡 [DEBUG] BackgroundSyncService exposed to window. Use window.backgroundSyncService.checkSignalRStatus() to check SignalR connection');
    }
  }

  initializeApp() {
    this.platform.ready().then(async () => {
      // create storage for indexdb
      this.sharedDataService.indexDbStorage = await this.storage.create();

      this.statusBar.styleDefault();
      this.sharedDataService.apiServiceRerence = this.apiService;
      this.sharedDataService.accountServiceRef = this.accountService;

      this.sharedDataService.offlineMode =
        localStorage.getItem(
          EnumService.LocalStorageKeys.OFFLINE_MODE_ENABLE
        ) === "true"
          ? true
          : false;

      if (this.sharedDataService.offlineMode) {
        this.offlineManagerService
          .getDeviceDetail()
          .then((deviceDetail: DeviceDetailData) => {
            this.offlineManagerService.offlineDeviceDetailData = deviceDetail;
          })
          .catch((error) => { });
      }

      const appLang = localStorage.getItem(
        EnumService.LocalStorageKeys.APP_LANGUAGE
      );
      if (appLang) {
        this.translateService.use(appLang);
      }

      this.sharedDataService.translateService = this.translateService;
      this.utilService.translateService = this.translateService;

      App.addListener("appRestoredResult", (data: any) => {
        this.observablesService.publishSomeData(
          EnumService.ObserverKeys.APP_RESTORED_RESULT,
          data.data
        );
      });

      App.addListener("backButton", (data: any) => { });

      this.setupDeepLink();
      this.loadAppSettings();
      setTimeout(() => {
        this.ngZone.run(() => {
          SplashScreen.hide();
          this.badge.clear();
        });
      }, 1500);

      // if (UtilService.isLocalHost()) {
      // setTimeout(() => {
      // this.router.navigate(['new-account-setup'], {
      // 	queryParams: {
      // 		userId: 'e243237f-3147-4810-9dbd-6f5ea7bffb30',
      // 	},
      // });
      // this.router.navigate(['forgot-password-reset'], {
      // 	queryParams: {
      // 		code: '7KRI18Y6',
      // 	},
      // });
      // }, 5000);
      // }
    });
  }

  appSettingLoaded = (isDeviceAssignedForDedicatedMode, data = null) => {
    console.log('🔧 [APP SETTINGS] appSettingLoaded called, isDeviceAssignedForDedicatedMode:', isDeviceAssignedForDedicatedMode);
    
    this.ngZone.run(() => {
      this.utilService.hideLoading();

      if (isDeviceAssignedForDedicatedMode) {
        console.log('🔧 [APP SETTINGS] Device is assigned for dedicated mode, calling configureAppForDedicatedMode');
        // If already personal mode user logged in then logout it
        if (
          !this.sharedDataService.dedicatedMode &&
          this.accountService.userValue &&
          this.accountService.userValue?.userId
        ) {
          this.accountService.logout(
            this.accountService.userValue.userId,
            false
          );
        }
        this.configureAppForDedicatedMode(data);
      } else {
        // If already dedicated mode then clear dedicated mode data
        if (this.sharedDataService.dedicatedMode) {
          this.sharedDataService.dedicatedModeDeviceDeleted();
        }
        this.configureAppForPersonalMode();
      }
    });
  };

  async loadAppSettings() {
    this.utilService.presentLoadingWithOptions();
    this.getDeviceUniqueId(() => {
      this.checkForAccessKey(() => {
        this.checkForToken(() => {
          // Version check needs the API token, so it starts once the token is available
          this.appUpdateService.init();
          if (!environment.isFormPreview) {
            this.apiService.getTimeZoneList().subscribe(() => { });
            if (UtilService.isWebApp()) {
              this.checkDeviceForDeticatedMode(({ isDeviceAssigned, data }) => {
                this.appSettingLoaded(isDeviceAssigned, data);
              });
            }
            else {
              if (this.sharedDataService.isTablet) {
                this.checkDeviceForDeticatedMode(
                  ({ isDeviceAssigned, data }) => {
                    this.appSettingLoaded(isDeviceAssigned, data);
                  }
                );
              } else {
                this.appSettingLoaded(false);
              }
            }
          } else {
            this.utilService.hideLoading();
          }
        });
      });
    });
  }

  getDeviceUniqueId = (callBack) => {
    if (!environment.isFormPreview && UtilService.isWebApp()) {
      const currentUrl =
        localStorage.getItem(EnumService.LocalStorageKeys.WINDOW_LOCATION) ||
        window.location.href;
      const urlPair = UtilService.getQueryStringValue(currentUrl, "URLPair");
      if (urlPair) {
        this.sharedDataService.deviceUID = urlPair;
      } else {
        this.sharedDataService.deviceUID = UtilService.uniqueIdForWebApp();
      }
      UtilService.fireCallBack(callBack);
    } else {
      // this.sharedDataService.deviceUID =
      //     "5A8CD1FF-24AE-44B9-A2AD-65AA5309E2CE";
      // this.sharedDataService.isTablet = true;
      // UtilService.fireCallBack(callBack);
      // return;
      this.uniqueDeviceID
        .get()
        .then((uuid: any) => {
          console.log('uuid', uuid);
          if (uuid && uuid.length > 0) {
            if (this.platform.is("ios") || this.platform.is("ipad") || this.platform.is("iphone")) {
              this.sharedDataService.deviceUID = uuid;
            } else {
              this.sharedDataService.deviceUID = this.device.uuid;
            }
          } else {
            this.sharedDataService.deviceUID = "";
          }

          UtilService.fireCallBack(callBack);
        })
        .catch((error: any) => {
          if (this.platform.is('android')) {
            this.sharedDataService.deviceUID = this.device.uuid;
          }
          console.log('errorerror', error);
          if (UtilService.isLocalHost()) {
            this.sharedDataService.deviceUID =
              "5A8CD1FF-24AE-44B9-A2AD-65AA5309E2CE";
            // this.sharedDataService.deviceUID =
            //     "C71D4229-E9B7-4EBD-A23F-B0FA259D788F";
            // this.sharedDataService.deviceUID =
            //   "0eab5395-a994-520b-5352-317105139432";
          }
          UtilService.fireCallBack(callBack);
        });
    }
  };

  setupDeepLink = () => {
    if (!environment.isFormPreview) {
      // reset password url https://cg.utopia-test.com/Login/ResetPassword?code=TTQ4LOM8
      // setup new account https://cg.utopia-test.com/Login/AccountSetup/545a1db3-f91c-48eb-be17-b9e4dd346322
      //
      // reset password url https://login.be-safetech.com/Login/ResetPassword?code=TTQ4LOM8
      // setup new account https://login.be-safetech.com/Login/AccountSetup/545a1db3-f91c-48eb-be17-b9e4dd346322

      App.addListener("appUrlOpen", (data: any) => {
        this.ngZone.run(() => {
          // Example url: https://beerswift.app/tabs/tab2
          // slug = /tabs/tab2
          const url = data.url;

          console.log("AppComponent->APP_URL_OPEN ", url);

          if (url.indexOf("ResetPassword") !== -1) {
            this.sharedDataService.isNavigationTypeDeepLink = true;
            const code = UtilService.getQueryStringValue(url, "code");
            this.router.navigate(["forgot-password-reset"], {
              queryParams: {
                code,
              },
            });
          } else if (url.indexOf("AccountSetup") !== -1) {
            this.sharedDataService.isNavigationTypeDeepLink = true;
            const userId = url.split("/").pop();
            this.router.navigate(["new-account-setup"], {
              queryParams: {
                userId,
              },
            });
          }
        });
      });
    }
  };

  checkForAccessKey = async (callBack) => {
    if (!localStorage.getItem(EnumService.LocalStorageKeys.API_ACCESS_KEY)) {
      this.accountService.getAccessKey().subscribe(
        async (data) => {
          UtilService.fireCallBack(callBack);
        },
        async (error) => {
          UtilService.fireCallBack(callBack);
        }
      );
    } else {
      UtilService.fireCallBack(callBack);
    }
  };

  checkForToken = async (callBack) => {
    if (!localStorage.getItem(EnumService.LocalStorageKeys.API_TOKEN)) {
      this.accountService.getToken().subscribe(
        async (token) => {
          UtilService.fireCallBack(callBack);
        },
        async (error) => {
          UtilService.fireCallBack(callBack);
        }
      );
    } else {
      UtilService.fireCallBack(callBack);
    }
  };

  checkDeviceForDeticatedMode = async (callBack) => {
    this.apiService
      .getDeviceEntityDetails(this.sharedDataService.deviceUID)
      .subscribe(
        async (data: Response) => {
          if (
            data.StatusCode === EnumService.ApiResponseCode.RequestSuccessful &&
            data.Result &&
            data.Result?.deviceDetailData &&
            data.Result?.deviceDetailData?.companyID
          ) {
            const isOfflineEnabled = data.Result.deviceDetailData.isOfflineEnabled ?? false; // fallback: default to false if field missing
            localStorage.setItem(EnumService.LocalStorageKeys.OFFLINE_ENABLED_KEY, String(isOfflineEnabled));
            UtilService.fireCallBack(callBack, {
              isDeviceAssigned: true,
              data,
            });
            this.apiService.getCompanyColor(data.Result?.deviceDetailData?.companyID).subscribe((response: any) => {
              if (response?.statusCode === 200) {
                this.sharedDataService.setCompanyColor(response?.result);
              }
            });
          } else {
            UtilService.fireCallBack(callBack, {
              isDeviceAssigned: false,
            });
          }
        },
        async (error) => {
          UtilService.fireCallBack(callBack, {
            isDeviceAssigned: this.sharedDataService.dedicatedMode,
          });
        }
      );
  };

  private isOfflineDatasetPopupAllowedRoute(): boolean {
    const url = this.router.url || "";
    return this.OFFLINE_DATASET_POPUP_ROUTES.some((route) =>
      url.includes(route)
    );
  }

  private async dismissDeviceOfflineModalSafely(): Promise<void> {
    if (!this.deviceOfflineModal) {
      return;
    }
    try {
      await this.deviceOfflineModal.dismiss();
    } catch (e) {
      // Modal might not be presented, ignore error
    }
    this.deviceOfflineModal = null;
  }

  private async dismissOfflineNotEnabledModalSafely(): Promise<void> {
    this.offlineNotEnabledModalCreating = false;
    if (!this.offlineNotEnabledModal) {
      return;
    }
    try {
      await this.offlineNotEnabledModal.dismiss();
    } catch (e) {
      // Modal might not be presented, ignore error
    }
    this.offlineNotEnabledModal = null;
  }

  /**
   * Check for incomplete sync and show modal with appropriate message
   */
  checkForIncompleteSync = async () => {
    const checkGeneration = ++this.incompleteSyncCheckGeneration;

    try {
      // Only check in dedicated mode
      if (!this.sharedDataService.dedicatedMode) {
        return;
      }

      // Check if sync state exists (indicating incomplete sync)
      const syncState = localStorage.getItem('background_sync_state');
      if (!syncState) {
        return; // No incomplete sync
      }

      console.log('⚠️ [INCOMPLETE SYNC] Incomplete sync detected. State:', syncState);

      // Check if database is empty
      let isDatabaseEmpty = false;
      try {
        if (this.backgroundSyncService) {
          isDatabaseEmpty = await this.backgroundSyncService.isDatabaseEmpty();
        }
      } catch (error) {
        console.error('❌ [INCOMPLETE SYNC] Error checking database state:', error);
        // Default to "not up to date" if check fails (safer assumption)
        isDatabaseEmpty = false;
      }

      if (checkGeneration !== this.incompleteSyncCheckGeneration) {
        return;
      }

      const { connected: isStillOnline } = await Network.getStatus();
      if (isStillOnline) {
        return;
      }

      // Get last sync date (might not exist if database is empty)
      const lastSyncDate = localStorage.getItem(
        EnumService.LocalStorageKeys.SYNC_DATE_TIME
      );

      // Determine message based on database state
      const message = isDatabaseEmpty 
        ? 'Database is empty'
        : 'Database is not up to date';

      console.log(`⚠️ [INCOMPLETE SYNC] ${message}. Database empty: ${isDatabaseEmpty}, Last sync: ${lastSyncDate || 'N/A'}`);

      if (!this.isOfflineDatasetPopupAllowedRoute()) {
        return;
      }

      if (checkGeneration !== this.incompleteSyncCheckGeneration) {
        return;
      }

      await this.dismissDeviceOfflineModalSafely();

      // Create and show incomplete sync modal
      this.deviceOfflineModal = await this.modalController.create({
        component: DeviceOfflinePage,
        cssClass: "offline-device-modal",
        animated: false,
        componentProps: {
          incompleteSyncMessage: message,
          isIncompleteSync: true
        }
      });

      this.deviceOfflineModal.onDidDismiss().then(() => {
        this.deviceOfflineModal = null;
      });

      await this.deviceOfflineModal.present();
      console.log(`✅ [INCOMPLETE SYNC] Modal presented with message: ${message}`);
    } catch (error) {
      console.error('❌ [INCOMPLETE SYNC] Error checking for incomplete sync:', error);
    }
  };

  checkForNetwork = async (knownOnlineStatus?: boolean) => {
    const wasOnline = this.previousNetworkStatus;
    const isOnline = knownOnlineStatus !== undefined ? knownOnlineStatus : (await Network.getStatus()).connected;
    const wentOffline = wasOnline && !isOnline;
    const wentOnline = !wasOnline && isOnline;

    const isOfflineModeEnable = localStorage.getItem(
      EnumService.LocalStorageKeys.OFFLINE_MODE_ENABLE
    );
    const isDedicatedMode = this.sharedDataService.dedicatedMode;

    try {
      // Handle dedicated mode: update offlineMode based on network status
      if (isDedicatedMode) {
        // Determine whether offline mode is allowed for this device
        const deviceDetail = this.offlineManagerService.offlineDeviceDetailData;
        const storedFlag = localStorage.getItem(EnumService.LocalStorageKeys.OFFLINE_ENABLED_KEY);
        const isOfflineEnabled = deviceDetail?.isOfflineEnabled ?? (storedFlag !== null ? storedFlag !== 'false' : false);

        if (!isOfflineEnabled) {
          // Offline mode is disabled for this device — show/dismiss blocking popup
          if (!isOnline) {
            if (!this.offlineNotEnabledModal && !this.offlineNotEnabledModalCreating) {
              try {
                this.offlineNotEnabledModalCreating = true;
                const modal = await this.modalController.create({
                  component: DeviceOfflinePage,
                  cssClass: 'offline-device-modal',
                  animated: false,
                  backdropDismiss: false,
                  componentProps: { isOfflineNotEnabled: true },
                });
                this.offlineNotEnabledModalCreating = false;

                // Network may have recovered while the modal was being created
                const { connected: stillOnline } = await Network.getStatus();
                if (stillOnline) {
                  try {
                    await modal.dismiss();
                  } catch (e) {
                    // Modal was never presented
                  }
                  return;
                }

                this.offlineNotEnabledModal = modal;
                modal.onDidDismiss().then(() => {
                  if (this.offlineNotEnabledModal === modal) {
                    this.offlineNotEnabledModal = null;
                  }
                });
                await modal.present();
              } catch (e) {
                console.error('[OFFLINE] modal create/present error:', e);
                this.offlineNotEnabledModalCreating = false;
                this.offlineNotEnabledModal = null;
              }
            }
          } else {
            await this.dismissOfflineNotEnabledModalSafely();
            // Offline was disabled by admin — ensure offlineMode is cleared so tiles are not greyed out
            if (this.sharedDataService.offlineMode) {
              this.sharedDataService.offlineMode = false;
              localStorage.removeItem(EnumService.LocalStorageKeys.OFFLINE_MODE_ENABLE);
              this.observablesService.publishSomeData(
                EnumService.ObserverKeys.OFFLINE_MODE_CHANGE,
                false
              );
            }
          }
          return;
        }

        const previousOfflineMode = this.sharedDataService.offlineMode;
        // Set offlineMode to false when online, true when offline
        this.sharedDataService.offlineMode = !isOnline;

        // Notify other components if offline mode changed
        if (previousOfflineMode !== this.sharedDataService.offlineMode) {
          console.log(`🌐 [NETWORK] Dedicated mode: ${isOnline ? 'ONLINE' : 'OFFLINE'}, offlineMode set to: ${this.sharedDataService.offlineMode}`);
          this.observablesService.publishSomeData(
            EnumService.ObserverKeys.OFFLINE_MODE_CHANGE,
            this.sharedDataService.offlineMode
          );
        }

        if (wentOnline) {
          this.incompleteSyncCheckGeneration++;
          await this.dismissDeviceOfflineModalSafely();
        }

        if (wentOffline) {
          this.checkForIncompleteSync();
        }
      } else if (isOfflineModeEnable === "true") {
        // Non-dedicated mode with offline mode enabled
        this.sharedDataService.offlineMode = true;
        this.offlineManagerService
          .getDeviceDetail()
          .then((deviceDetail: DeviceDetailData) => {
            this.offlineManagerService.offlineDeviceDetailData = deviceDetail;
          })
          .catch((error) => { });
      } else {
        // Non-dedicated mode: show/hide offline modal
        if (!this.deviceOfflineModal) {
          this.deviceOfflineModal = await this.modalController.create({
            component: DeviceOfflinePage,
            cssClass: "offline-device-modal",
            animated: false,
          });
        }
        this.deviceOfflineModal?.onDidDismiss()?.then(() => {
          this.deviceOfflineModal = null;
        });

        if (isOnline) {
          this.incompleteSyncCheckGeneration++;
          await this.dismissDeviceOfflineModalSafely();
        } else if (wentOffline && this.isOfflineDatasetPopupAllowedRoute()) {
          await this.deviceOfflineModal.present();
        } else if (!this.isOfflineDatasetPopupAllowedRoute()) {
          await this.dismissDeviceOfflineModalSafely();
        }
      }
    } finally {
      this.previousNetworkStatus = isOnline;
    }
  };

  configureAppForDedicatedMode = async (data: Response = null) => {
    console.log('🚀 [DEDICATED MODE] configureAppForDedicatedMode called');
    
    localStorage.setItem(
      EnumService.LocalStorageKeys.IS_DEDICATED_MODE,
      "true"
    );

    // Check  previous sync data progress and clear if not finished
    this.offlineManagerService.checkPreviousSyncDataProgress();

    this.sharedDataService.dedicatedMode = true;

    this.checkForNetwork();

    Network.addListener("networkStatusChange", (status) => {
      this.ngZone.run(async () => {
        console.log("Network status changed", status);

        const wasOnline = this.previousNetworkStatus;
        const isNowOnline = status.connected;
        const wentOnline = isNowOnline && !wasOnline;
        const wentOffline = !isNowOnline && wasOnline;

        await this.checkForNetwork(status.connected);

        // Trigger background sync when network comes back online (only if offline mode is enabled)
        const offlineEnabledFlag = localStorage.getItem(EnumService.LocalStorageKeys.OFFLINE_ENABLED_KEY);
        const isOfflineEnabledNow = offlineEnabledFlag !== 'false';
        if (wentOnline && this.backgroundSyncService) {
          // Silently refresh isOfflineEnabled from server to catch any admin-side changes made while offline
          this.apiService.getDeviceEntityDetails(this.sharedDataService.deviceUID).subscribe(
            (data: Response) => {
              if (data?.StatusCode === EnumService.ApiResponseCode.RequestSuccessful && data?.Result?.deviceDetailData) {
                const refreshed = data.Result.deviceDetailData.isOfflineEnabled ?? false;
                localStorage.setItem(EnumService.LocalStorageKeys.OFFLINE_ENABLED_KEY, String(refreshed));
              }
            },
            () => {}
          );

          setTimeout(() => {
            if (isOfflineEnabledNow) {
              // Offline enabled — run full sync
              this.backgroundSyncService.startBackgroundSync().catch((error) => {
                console.error('Background sync on network change failed:', error);
              });
            } else {
              // Offline disabled — only check for pending changes (e.g. isOfflineEnabled may have flipped to true)
              this.backgroundSyncService.checkPendingChanges().catch((error) => {
                console.error('Pending changes check on network change failed:', error);
              });
            }
          }, 2000);

          const toast = await this.toastController.create({
            message: 'Device is back online',
            duration: 3000,
            position: 'bottom',
            color: 'success',
            cssClass: 'network-status-toast'
          });
          await toast.present();
        } else if (wentOffline) {
          const toast = await this.toastController.create({
            message: 'Device is now offline',
            duration: 3000,
            position: 'bottom',
            color: 'danger',
            cssClass: 'network-status-toast'
          });
          await toast.present();
        }
      });
    });

    const isOfflineEnabled = data?.Result?.deviceDetailData?.isOfflineEnabled ?? false;

    if (isOfflineEnabled) {
      console.log('🚀 [DEDICATED MODE] Setting up database...');
      this.offlineManagerService.dbSetUp();
      console.log('🚀 [DEDICATED MODE] Database setup initiated');
    } else {
      console.log('🚀 [DEDICATED MODE] isOfflineEnabled is false — skipping DB setup and sync');
    }

    // Wait for database setup, then check if sync is needed before navigation
    // Use a longer delay and retry mechanism to ensure database is ready
    const checkAndSync = async (retryCount = 0) => {
      console.log(`📱 [DEDICATED MODE] checkAndSync called, retry: ${retryCount}`);
      
      if (!this.backgroundSyncService) {
        console.log('📱 [DEDICATED MODE] BackgroundSyncService not available, retrying...');
        if (retryCount < 5) {
          setTimeout(() => checkAndSync(retryCount + 1), 500);
        }
        return;
      }
      
      console.log('📱 [DEDICATED MODE] BackgroundSyncService is available');

      try {
        // Check network status
        const networkStatus = await Network.getStatus();
        const isOnline = networkStatus.connected;
        console.log('📱 [DEDICATED MODE] Network status:', isOnline);

        // Check if database is empty (with retry if database not ready)
        let isEmpty = false;
        try {
          console.log('📱 [DEDICATED MODE] Calling isDatabaseEmpty()...');
          isEmpty = await this.backgroundSyncService.isDatabaseEmpty();
          console.log('📱 [DEDICATED MODE] isDatabaseEmpty() returned:', isEmpty);
        } catch (dbError) {
          console.log('📱 [DEDICATED MODE] Database check failed, retrying...', dbError);
          // Retry after a short delay if database isn't ready yet
          if (retryCount < 5) {
            setTimeout(() => checkAndSync(retryCount + 1), 500);
            return;
          }
          // After 5 retries, assume empty and proceed
          console.log('📱 [DEDICATED MODE] Max retries reached, assuming database is empty');
          isEmpty = true;
        }

        console.log('📱 [DEDICATED MODE] Final check - Database empty:', isEmpty, 'Online:', isOnline, 'Retry:', retryCount);

        // If database is empty and online, perform sync before navigation
        if (isEmpty && isOnline) {
          console.log('📱 [DEDICATED MODE] ✅ Conditions met: Empty DB + Online → Starting initial sync...');
          try {
            await this.backgroundSyncService.startBackgroundSync();
            console.log('📱 [DEDICATED MODE] ✅ Initial sync completed, proceeding to navigation');
          } catch (error) {
            console.error('📱 [DEDICATED MODE] ❌ Initial sync failed:', error);
            // Continue navigation even if sync fails - user can retry later
          }
        } else if (!isEmpty) {
          // Database has data, trigger incremental sync in background (non-blocking)
          console.log('📱 [DEDICATED MODE] Database has data, starting incremental sync in background');
          this.backgroundSyncService.startBackgroundSync().catch((error) => {
            console.error('Background sync failed:', error);
          });
          // Periodic check will start automatically after sync completes
        } else {
          console.log('📱 [DEDICATED MODE] Database empty but offline - will sync when online');
        }
      } catch (error) {
        console.error('📱 [DEDICATED MODE] Error in checkAndSync:', error);
        // Retry if we haven't exceeded retry limit
        if (retryCount < 5) {
          setTimeout(() => checkAndSync(retryCount + 1), 1000);
        }
      }
    };

    // Start checking after database setup (with initial delay) — only if offline mode is enabled
    if (isOfflineEnabled) {
      console.log('📱 [DEDICATED MODE] Scheduling checkAndSync in 2 seconds...');
      setTimeout(() => {
        console.log('📱 [DEDICATED MODE] Executing checkAndSync now...');
        checkAndSync(0);
      }, 2000); // Wait 2 seconds for database to be ready
    }

    // Note: SignalR connection is already initialized in BackgroundSyncService constructor
    // Only ensure it if it's not already connected (to avoid duplicate connections)
    setTimeout(() => {
      this.backgroundSyncService.ensureSignalRConnection();
    }, 3000); // Wait 3 seconds to ensure apiBaseUrl is set

    this.platform.resume.subscribe(() => {
      if (this.sharedDataService.dedicatedMode) {
        this.sharedDataService.checkLocationArchiveForDedicatedMode();
        
        // Trigger background sync when app resumes
        if (this.backgroundSyncService) {
          setTimeout(() => {
            this.backgroundSyncService.startBackgroundSync().catch((error) => {
              console.error('Background sync on resume failed:', error);
            });
          }, 1000);
        }
      } else {
        this.sharedDataService.checkLocationArchiveForPersonalMode();
      }
    });

    this.observablesService
      .getObservable(EnumService.ObserverKeys.OFFLINE_DATA_SYNC_NEEDED)
      .subscribe(async () => {
        const networkStatus = await Network.getStatus();
        if (networkStatus.connected && !this.sharedDataService.offlineMode) {
          this.backgroundSyncService.syncPendingSignOffs().catch(() => {});
        }
      });

    this.observablesService
      .getObservable(EnumService.ObserverKeys.CHECK_FOR_LOCATION_ARCHIVE)
      .subscribe(() => {
        if (this.sharedDataService.dedicatedMode) {
          this.sharedDataService.checkLocationArchiveForDedicatedMode();
        } else {
          this.sharedDataService.checkLocationArchiveForPersonalMode();
        }
      });

    if (data) {
      localStorage.setItem(
        EnumService.LocalStorageKeys.DEDICATED_MODE_DEVICE_DETAIL,
        JSON.stringify(data.Result?.deviceDetailData)
      );
      localStorage.setItem(
        EnumService.LocalStorageKeys.DEDICATED_MODE_ASSIGNED_ENTITIES,
        JSON.stringify(data.Result?.deviceEntityData)
      );

      this.sharedDataService.dedicatedModeDeviceDetailData =
        data.Result?.deviceDetailData;
      this.sharedDataService.dedicatedModeAssignedEntities =
        data.Result?.deviceEntityData;

      this.sharedDataService.validateDedicatedModeLocationUse(
        this.sharedDataService.dedicatedModeAssignedEntities
      );

      if (
        !this.sharedDataService.dedicatedModeLocationUse &&
        this.sharedDataService.dedicatedModeAssignedEntities &&
        this.sharedDataService.dedicatedModeAssignedEntities.length === 1
      ) {
        this.sharedDataService.dedicatedModeLocationUse =
          this.sharedDataService.dedicatedModeAssignedEntities[0];
        localStorage.setItem(
          EnumService.LocalStorageKeys.DEDICATED_MODE_LOCATION_USE,
          JSON.stringify(
            this.sharedDataService.dedicatedModeAssignedEntities[0]
          )
        );
      }
    }

    try {
      if (!UtilService.isWebApp()) {
        this.screenOrientation.lock(
          this.screenOrientation.ORIENTATIONS.LANDSCAPE
        );
      }
    } catch (e) { }

    if (!this.sharedDataService.isNavigationTypeDeepLink) {
      if (this.sharedDataService.dedicatedModeLocationUse) {
        this.utilService.presentLoadingWithOptions();
        this.sharedDataService.checkLocationArchiveForDedicatedMode(() => {
          this.utilService.hideLoading();
          // Ensure sync has completed before navigating to dashboard
          // The checkAndSync function should have already run, but wait a bit more if needed
          setTimeout(() => {
            this.navController.navigateRoot("dashboard-dm", {
              replaceUrl: true,
            });
          }, 500);
        });
      } else {
        this.navController.navigateRoot("choose-location");
      }
    }

    setTimeout(() => {
      this.configureForLocation();
    }, 1000);

    this.sharedDataService.updatePushSettingOnServer(false);
  };

  configureAppForPersonalMode = async () => {
    // await this.auth.init();

    try {
      if (!UtilService.isWebApp()) {
        this.screenOrientation.lock(
          this.screenOrientation.ORIENTATIONS.PORTRAIT
        );
      }
    } catch (e) { }

    this.platform.resume.subscribe(() => {
      this.sharedDataService.checkLocationArchiveForPersonalMode();
    });

    this.observablesService
      .getObservable(EnumService.ObserverKeys.CHECK_FOR_LOCATION_ARCHIVE)
      .subscribe(() => {
        this.sharedDataService.checkLocationArchiveForPersonalMode();
      });

    if (!this.sharedDataService.isNavigationTypeDeepLink) {
      const user: User = this.accountService.userValue;
      if (user?.userId && !user.isMobileSessionExpiration) {
        this.utilService.presentLoadingWithOptions();
        this.sharedDataService.checkLocationArchiveForPersonalMode(() => {
          this.utilService.hideLoading();
          this.navController.navigateRoot("/tabs/dashboard", {
            replaceUrl: true,
          });

          // if (UtilService.isLocalHost()) {
          // 	this.navController.navigateRoot('/form-riskassessment', {
          // 		replaceUrl: true,
          // 	});
          // }

          if (!this.sharedDataService.isLoginAfterAppOpen && user?.companyID) {
            this.accountService
              .checkMobileSessionExpirationSetting(user?.companyID)
              .subscribe((response: Response) => {
                if (
                  response.StatusCode ===
                  EnumService.ApiResponseCode.RequestSuccessful
                ) {
                  if (response.Result?.isMobileSessionExpiration) {
                    this.navController.navigateRoot("/login", {
                      replaceUrl: true,
                    });
                  }
                }
              });
          }
        });
      } else {
        if (
          window.location.pathname !== "/forgot-password" &&
          window.location.pathname !== "/auth/callback" &&
          window.location.pathname !== "/auth/logout" &&
          window.location.pathname !== "/checkoktaenable"
        ) {
          this.navController.navigateRoot("/login");
        }
      }
    }

    localStorage.removeItem(EnumService.LocalStorageKeys.IS_DEDICATED_MODE);

    setTimeout(() => {
      this.sharedDataService.configureForPushNotification();
      this.configureForLocation();
    }, 1000);
  };

  configureForLocation = async () => {
    try {
      const locationPermission = await Geolocation.checkPermissions();

      if (
        locationPermission.location !== "granted" &&
        Geolocation &&
        Geolocation.requestPermissions
      ) {
        await Geolocation.requestPermissions();
      }

      Geolocation.getCurrentPosition()
        .then((response) => {
          if (response) {
            this.sharedDataService.myCurrentGeoLocation = response;
            console.log("Current Location CP", response);
          }
        })
        .catch((error) => {
          console.log("GetCurrentPosition Error ", error);
        });
    } catch (e) { }

    try {
      Geolocation.watchPosition({}, (response) => {
        if (response) {
          this.sharedDataService.myCurrentGeoLocation = response;
          console.log("Current Location WP", response);
        }
      });
    } catch (e) {
      console.log("WatchPosition Error ", e);
    }
  };
};
